/**
 * Confirm dialog shown after the vendor drags out a date range on a car lane.
 * Captures an optional reason and persists the new unavailable_range via the
 * parent's save handler. Iter 94 — supports repeating the block weekly
 * (e.g., every Sunday) for a chosen number of weeks in one step.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Repeat } from "lucide-react";
import { formatRangeDay } from "@/components/calendar/calendarHelpers";

const REPEAT_OPTIONS = [
  [4, "4 weeks (1 month)"],
  [8, "8 weeks (2 months)"],
  [13, "13 weeks (3 months)"],
  [26, "26 weeks (6 months)"],
  [52, "52 weeks (1 year)"],
];

export default function CalendarBlockDialog({
  open,
  car,
  startIso,
  endIso,
  days,
  reason,
  setReason,
  onCancel,
  onSave,
  saving,
}) {
  const [repeat, setRepeat] = useState(false);
  const [repeatWeeks, setRepeatWeeks] = useState(13);
  useEffect(() => { if (open) { setRepeat(false); setRepeatWeeks(13); } }, [open]);

  const weekday = startIso
    ? new Date(startIso + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long" })
    : "";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-testid="cal-pending-dialog">
        <DialogHeader>
          <DialogTitle>Block these dates?</DialogTitle>
          <DialogDescription>
            {car && (
              <>
                <span className="font-medium text-neutral-900">{car.make} {car.model}</span> will be unavailable from{" "}
                <span className="font-medium text-neutral-900">{formatRangeDay(startIso)}</span> to{" "}
                <span className="font-medium text-neutral-900">{formatRangeDay(endIso)}</span> ({days} day{days === 1 ? "" : "s"}). Customers won't be able to book these dates.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional) — e.g. service, vacation"
          className="rounded-lg"
          data-testid="cal-pending-reason"
          autoFocus
        />
        {/* Iter 94 — recurring weekly block (e.g., every Sunday) */}
        <div className="rounded-xl border border-border bg-neutral-50/60 p-3 space-y-2.5">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <Checkbox
              checked={repeat}
              onCheckedChange={(v) => setRepeat(!!v)}
              data-testid="cal-pending-repeat"
            />
            <span className="text-sm font-medium inline-flex items-center gap-1.5">
              <Repeat className="h-3.5 w-3.5 text-neutral-500" />
              Repeat weekly{weekday ? ` — every ${weekday}` : ""}
            </span>
          </label>
          {repeat && (
            <div className="flex items-center gap-2 pl-7">
              <span className="text-xs text-neutral-500">For</span>
              <select
                value={repeatWeeks}
                onChange={(e) => setRepeatWeeks(Number(e.target.value))}
                className="text-sm rounded-lg border border-border bg-white px-2 py-1.5"
                data-testid="cal-pending-repeat-weeks"
              >
                {REPEAT_OPTIONS.map(([w, label]) => (
                  <option key={w} value={w}>{label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} className="rounded-full" data-testid="cal-pending-cancel">
            Cancel
          </Button>
          <Button onClick={() => onSave(repeat ? { repeatWeeks } : {})} disabled={saving} className="rounded-full" data-testid="cal-pending-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            {repeat ? `Block ${repeatWeeks} weeks` : "Block dates"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
